import { useEffect, useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import { tipoStatusProgramadoDominanteNoDia } from '../lib/statusProgramadoUtils';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export function ymdFromParts(y: number, month0: number, d: number): string {
  return `${y}-${pad2(month0 + 1)}-${pad2(d)}`;
}

function getDaysInMonth(year: number, month0: number): { days: number; startPad: number } {
  const first = new Date(year, month0, 1);
  const last = new Date(year, month0 + 1, 0);
  return { days: last.getDate(), startPad: first.getDay() };
}

function cmpYmd(a: string, b: string): number {
  return a.localeCompare(b);
}

function dayEmAlgumPeriodo(dayStr: string, periodos: { de: string; ate: string }[] | undefined): boolean {
  if (!periodos?.length) return false;
  for (const r of periodos) {
    const lo = cmpYmd(r.de, r.ate) <= 0 ? r.de : r.ate;
    const hi = cmpYmd(r.de, r.ate) <= 0 ? r.ate : r.de;
    if (cmpYmd(dayStr, lo) >= 0 && cmpYmd(dayStr, hi) <= 0) return true;
  }
  return false;
}

function shiftYmd(dayStr: string, deltaDays: number): string | null {
  const p = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayStr.trim());
  if (!p) return null;
  const dt = new Date(Number(p[1]), Number(p[2]) - 1, Number(p[3]));
  dt.setDate(dt.getDate() + deltaDays);
  return ymdFromParts(dt.getFullYear(), dt.getMonth(), dt.getDate());
}

/** Bordas arredondadas só nas pontas de um segmento contíguo no calendário. */
function roundedSegmentClasses(isSegStart: boolean, isSegEnd: boolean): string {
  if (isSegStart && isSegEnd) return 'rounded-lg';
  if (isSegStart) return 'rounded-l-lg rounded-r-none';
  if (isSegEnd) return 'rounded-r-lg rounded-l-none';
  return 'rounded-none';
}

function labelTipoAgendaCal(tipo: string): string {
  switch (tipo) {
    case 'naviex':
      return 'Destaque Naviex';
    case 'principal':
      return 'Destaque Principal';
    case 'categoria':
      return 'Destaque Categoria';
    case 'ativada':
    case 'ativado':
      return 'Ativada';
    case 'desativada':
    case 'desativado':
      return 'Desativada';
    default:
      return tipo;
  }
}

/** Estilo de célula alinhado aos botões de status programado (notícias / admin). */
function classesCorTipoAgenda(tipo: string): string {
  switch (tipo) {
    case 'naviex':
      return 'bg-violet-200/95 text-violet-950 border-y border-violet-400/75 border-l border-violet-400/75 border-r border-violet-300/50';
    case 'principal':
      return 'bg-amber-200/95 text-amber-950 border-y border-amber-400/75 border-l border-amber-400/75 border-r border-amber-300/50';
    case 'categoria':
      return 'bg-orange-200/95 text-orange-950 border-y border-orange-400/75 border-l border-orange-400/75 border-r border-orange-300/50';
    case 'ativada':
    case 'ativado':
      return 'bg-emerald-200/95 text-emerald-950 border-y border-emerald-400/75 border-l border-emerald-400/75 border-r border-emerald-300/50';
    case 'desativada':
    case 'desativado':
      return 'bg-red-200/95 text-red-950 border-y border-red-400/75 border-l border-red-400/75 border-r border-red-300/50';
    default:
      return 'bg-slate-200/95 text-slate-900 border-y border-slate-400/60 border-l border-slate-400/60 border-r border-slate-300/50';
  }
}

export type PeriodoDataCalendarProps = {
  minDate: string;
  periodoDe: string;
  periodoAte: string;
  onChange: (de: string, ate: string) => void;
  className?: string;
  /** Dias já cobertos: não clicáveis, estilo laranja (ex.: experiências). */
  periodosBloqueados?: { de: string; ate: string }[];
  /**
   * Agendamentos existentes (com `tipo`): pinta cada dia com a cor do tipo dominante no dia;
   * continua clicável (exceto se também estiver em `periodosBloqueados`).
   */
  periodosAgendaVisual?: { de: string; ate: string; tipo: string }[];
  /** Dia de referência (ex. “hoje” do status programado): realça o dia se tiver agendamento. */
  diaReferenciaAgendaVisual?: string;
};

/**
 * Período contíguo: 1º clique = início, 2º = fim. Duplo clique (ou 2× o mesmo dia) = um só dia.
 * Com `periodoDe` e `periodoAte` já preenchidos, o próximo clique simples limpa tudo e usa só esse dia como novo início (âncora).
 */
export function PeriodoDataCalendar({
  minDate,
  periodoDe,
  periodoAte,
  onChange,
  className = '',
  periodosBloqueados,
  periodosAgendaVisual,
  diaReferenciaAgendaVisual,
}: PeriodoDataCalendarProps) {
  const [cursorY, setCursorY] = useState(() => {
    const t = (periodoDe || minDate).slice(0, 10);
    const p = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
    if (!p) return new Date().getFullYear();
    return Number(p[1]);
  });
  const [cursorM0, setCursorM0] = useState(() => {
    const t = (periodoDe || minDate).slice(0, 10);
    const p = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
    if (!p) return new Date().getMonth();
    return Number(p[2]) - 1;
  });
  const [anchor, setAnchor] = useState<string | null>(null);

  useEffect(() => {
    const t = (periodoDe || minDate).slice(0, 10);
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
    if (m) {
      setCursorY(Number(m[1]));
      setCursorM0(Number(m[2]) - 1);
    }
  }, [periodoDe, minDate]);

  const { days, startPad } = useMemo(() => getDaysInMonth(cursorY, cursorM0), [cursorY, cursorM0]);

  const tipoDominantePorDiaNoMes = useMemo(() => {
    const map = new Map<string, string>();
    if (!periodosAgendaVisual?.length) return map;
    for (let d = 1; d <= days; d++) {
      const dayStr = ymdFromParts(cursorY, cursorM0, d);
      if (cmpYmd(dayStr, minDate) < 0) continue;
      const t = tipoStatusProgramadoDominanteNoDia(periodosAgendaVisual, dayStr);
      if (t) map.set(dayStr, t);
    }
    return map;
  }, [cursorY, cursorM0, days, periodosAgendaVisual, minDate]);

  const rangeLo = useMemo(() => {
    const a = (periodoDe || '').trim();
    const b = (periodoAte || '').trim();
    if (!a) return null;
    if (!b || a === b) return a;
    return cmpYmd(a, b) <= 0 ? a : b;
  }, [periodoDe, periodoAte]);

  const rangeHi = useMemo(() => {
    const a = (periodoDe || '').trim();
    const b = (periodoAte || '').trim();
    if (!a) return null;
    if (!b || a === b) return a;
    return cmpYmd(a, b) <= 0 ? b : a;
  }, [periodoDe, periodoAte]);

  const mesTitulo = new Date(cursorY, cursorM0, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  const onDayClick = (e: MouseEvent, dayStr: string) => {
    if (cmpYmd(dayStr, minDate) < 0) return;
    if (dayEmAlgumPeriodo(dayStr, periodosBloqueados)) return;
    const detail = e.detail ?? 1;
    if (detail >= 2) {
      onChange(dayStr, dayStr);
      setAnchor(null);
      return;
    }
    const deTrim = (periodoDe || '').trim();
    const ateTrim = (periodoAte || '').trim();
    const hasCommittedSelection = deTrim !== '' && ateTrim !== '' && anchor === null;
    if (hasCommittedSelection) {
      onChange('', '');
      setAnchor(dayStr);
      return;
    }
    if (anchor === null) {
      setAnchor(dayStr);
      return;
    }
    if (anchor === dayStr) {
      onChange(dayStr, dayStr);
      setAnchor(null);
      return;
    }
    const de = cmpYmd(anchor, dayStr) <= 0 ? anchor : dayStr;
    const ate = cmpYmd(anchor, dayStr) <= 0 ? dayStr : anchor;
    onChange(de, ate);
    setAnchor(null);
  };

  const inBlockedSegment = (d: string) => dayEmAlgumPeriodo(d, periodosBloqueados);
  const inSelectionSegment = (d: string) =>
    !!(rangeLo && rangeHi && cmpYmd(d, rangeLo) >= 0 && cmpYmd(d, rangeHi) <= 0);

  const tipoVisualNoDia = (dayStr: string) => tipoDominantePorDiaNoMes.get(dayStr) ?? null;

  const cellClass = (dayStr: string) => {
    const base =
      'aspect-square max-h-10 sm:max-h-11 text-sm font-medium transition-colors flex items-center justify-center select-none';
    if (cmpYmd(dayStr, minDate) < 0) {
      return `${base} rounded-lg text-gray-300 cursor-not-allowed bg-gray-50`;
    }
    const tv = tipoVisualNoDia(dayStr);
    if (dayEmAlgumPeriodo(dayStr, periodosBloqueados)) {
      if (tv) {
        const prev = shiftYmd(dayStr, -1);
        const next = shiftYmd(dayStr, 1);
        const prevInB = prev && cmpYmd(prev, minDate) >= 0 && dayEmAlgumPeriodo(prev, periodosBloqueados);
        const nextInB = next && dayEmAlgumPeriodo(next, periodosBloqueados);
        const prevT = prev && cmpYmd(prev, minDate) >= 0 && prevInB ? tipoVisualNoDia(prev) : null;
        const nextT = next && nextInB ? tipoVisualNoDia(next) : null;
        const segStart = tv !== prevT;
        const segEnd = tv !== nextT;
        const rnd = roundedSegmentClasses(segStart, segEnd);
        return `${base} ${rnd} cursor-not-allowed ${classesCorTipoAgenda(tv)} -mx-px opacity-[0.92]`;
      }
      const prevB = shiftYmd(dayStr, -1);
      const nextB = shiftYmd(dayStr, 1);
      const segStart = !prevB || cmpYmd(prevB, minDate) < 0 || !inBlockedSegment(prevB);
      const segEnd = !nextB || !inBlockedSegment(nextB);
      const rnd = roundedSegmentClasses(segStart, segEnd);
      return `${base} ${rnd} cursor-not-allowed bg-orange-200/95 text-orange-950 border-y border-orange-400/70 ${
        segStart ? 'border-l border-orange-400/70' : 'border-l border-orange-300/40'
      } ${segEnd ? 'border-r border-orange-400/70' : 'border-r border-orange-300/40'} -mx-px`;
    }
    if (anchor === dayStr) {
      return `${base} rounded-lg cursor-pointer bg-orange-200 text-orange-950 ring-2 ring-orange-500 ring-offset-1 z-[1]`;
    }
    const inRange = inSelectionSegment(dayStr);
    if (inRange && rangeLo && rangeHi) {
      const prevS = shiftYmd(dayStr, -1);
      const nextS = shiftYmd(dayStr, 1);
      const segStart = dayStr === rangeLo || !prevS || cmpYmd(prevS, minDate) < 0 || !inSelectionSegment(prevS);
      const segEnd = dayStr === rangeHi || !nextS || !inSelectionSegment(nextS);
      const rnd = roundedSegmentClasses(segStart, segEnd);
      return `${base} ${rnd} cursor-pointer bg-orange-100 text-orange-950 hover:bg-orange-200 border-y border-orange-200/80 ${
        segStart ? 'border-l border-orange-300' : 'border-l border-orange-100'
      } ${segEnd ? 'border-r border-orange-300' : 'border-r border-orange-100'} -mx-px ${
        segStart || segEnd ? 'ring-1 ring-orange-400/50' : ''
      }`;
    }
    if (tv) {
      const prev = shiftYmd(dayStr, -1);
      const next = shiftYmd(dayStr, 1);
      const prevT = prev && cmpYmd(prev, minDate) >= 0 ? tipoVisualNoDia(prev) : null;
      const nextT = next ? tipoVisualNoDia(next) : null;
      const segStart = tv !== prevT;
      const segEnd = tv !== nextT;
      const rnd = roundedSegmentClasses(segStart, segEnd);
      const vigRing =
        diaReferenciaAgendaVisual && dayStr === diaReferenciaAgendaVisual
          ? ' ring-2 ring-emerald-600 ring-offset-1 z-[1]'
          : '';
      return `${base} ${rnd} cursor-pointer ${classesCorTipoAgenda(tv)} -mx-px hover:brightness-[0.97]${vigRing}`;
    }
    return `${base} rounded-lg cursor-pointer bg-white text-gray-800 hover:bg-orange-50 border border-gray-200`;
  };

  const cells: ReactNode[] = [];
  for (let i = 0; i < startPad; i++) {
    cells.push(<span key={`pad-${i}`} className="aspect-square max-h-10 sm:max-h-11 bg-white" />);
  }
  for (let d = 1; d <= days; d++) {
    const dayStr = ymdFromParts(cursorY, cursorM0, d);
    const bloqueado = dayEmAlgumPeriodo(dayStr, periodosBloqueados);
    const tv = tipoVisualNoDia(dayStr);
    const titleAgenda = tv ? `Agendado: ${labelTipoAgendaCal(tv)}` : undefined;
    cells.push(
      bloqueado ? (
        <span
          key={dayStr}
          className={cellClass(dayStr)}
          title={titleAgenda ?? 'Data já coberta por um agendamento'}
          aria-disabled="true"
        >
          {d}
        </span>
      ) : (
        <button key={dayStr} type="button" onClick={(e) => onDayClick(e, dayStr)} className={cellClass(dayStr)} title={titleAgenda}>
          {d}
        </button>
      )
    );
  }

  return (
    <div className={`rounded-xl border border-gray-200 bg-white p-3 sm:p-4 shadow-sm ${className}`}>
      <div className="flex items-center justify-between mb-3 gap-2">
        <button
          type="button"
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-700"
          aria-label="Mês anterior"
          onClick={() => {
            if (cursorM0 === 0) {
              setCursorM0(11);
              setCursorY((y) => y - 1);
            } else setCursorM0((m) => m - 1);
          }}
        >
          ‹
        </button>
        <span className="text-sm font-semibold text-gray-800 capitalize">{mesTitulo}</span>
        <button
          type="button"
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-700"
          aria-label="Próximo mês"
          onClick={() => {
            if (cursorM0 === 11) {
              setCursorM0(0);
              setCursorY((y) => y + 1);
            } else setCursorM0((m) => m + 1);
          }}
        >
          ›
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] sm:text-xs font-semibold text-gray-500 mb-1">
        {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((l) => (
          <span key={l} className="py-1">
            {l}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px bg-gray-200/80 rounded-lg overflow-hidden p-px">{cells}</div>
      {(periodoDe || anchor) && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-gray-600">
          <span>
            Selecionado:{' '}
            <strong className="text-gray-900">
              {(() => {
                const lo = rangeLo;
                const hi = rangeHi;
                if (!lo) return anchor ? `${anchor} (escolha o fim)` : '—';
                if (!hi || lo === hi) return lo;
                return `${lo} — ${hi}`;
              })()}
            </strong>
          </span>
          <button
            type="button"
            className="text-orange-600 hover:underline font-medium"
            onClick={() => {
              setAnchor(null);
              onChange('', '');
            }}
          >
            Limpar
          </button>
        </div>
      )}
    </div>
  );
}
